import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTemplatesList, getWhatsappAccount, sendMarketingMessages, getPhoneGroups, getGroupPhoneNumbers, getGroupPhoneNumbersAll, getTemplate, scheduleGroupMessage } from "../../../api/login.service";
import { getCookie } from "../../../api/cookie";
import MediaSelectionPopup from "../../../components/MediaSelectionPopup";
import GroupSelectionPopup from "../../../components/GroupSelectionPopup";
import LoadingComponent from "../../../components/LoadingComponent";

interface MarketingTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  status: string;
  content: string;
  components?: any[];
}

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  phoneNumberId: string;
  businessName?: string;
  status?: string;
}

interface BulkSendPayload {
  templateId: string;
  templateName: string;
  phoneNumbers: string[];
  businessId?: string;
  variables?: Record<string, string>;
  components?: any[];
  scheduledTime?: string;
}

// Generate UUID for flow_token
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Build WhatsApp components payload with flow logic
function buildWAComponentsForMarketing(variables: Record<string, string>, template: MarketingTemplate): any[] {
  const components: any[] = [];
  
  // Add image header if template has one OR custom header URL is provided
  const hasImageHeader = template.components?.some((c: any) => c.type === "HEADER" && c.format === "IMAGE");
  if (hasImageHeader && (variables['header_url'] || template.components?.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")?.example?.header_handle?.[0])) {
    components.push({
      type: "header",
      parameters: [
        {
          type: "image",
          image: {
            link: variables['header_url'] || template.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")?.example?.header_handle?.[0]
          }
        }
      ]
    });
  }
  
  // Extract template variables from content
  const variableKeys = template.content ? (template.content.match(/\{\{([^}]+)\}\}/g) || []).map(v => v.replace(/\{\{|\}\}/g, '')) : [];
  
  // Add body parameters if there are variables
  if (variableKeys.length > 0) {
    const parameters = variableKeys.map((k) => ({
      type: "text",
      text: String(variables[k] ?? ""),
    }));
    components.push({ type: "body", parameters });
  }
  
  // Check if template has FLOW buttons and add them to components
  const buttonsComponent = template.components?.find((c: any) => c.type === "BUTTONS");
  
  if (buttonsComponent && Array.isArray(buttonsComponent.buttons) && buttonsComponent.buttons.length > 0) {
    // Find FLOW buttons specifically
    const flowButtons = buttonsComponent.buttons.filter((button: any) => button && button.type === "FLOW");
    
    // Only add flow button component if there are actual FLOW buttons
    if (flowButtons.length > 0) {
      flowButtons.forEach((button: any, flowIndex: number) => {
        // Find the original index of this FLOW button in the buttons array
        const originalIndex = buttonsComponent.buttons.findIndex((b: any) => b && b.type === "FLOW" && b === button);
        
        // Generate flow_token UUID for this flow button
        const flowToken = generateUUID();
        
        components.push({
          type: "button",
          sub_type: "flow",
          index: String(originalIndex >= 0 ? originalIndex : flowIndex),
          parameters: [
            {
              type: "action",
              action: {
                flow_token: flowToken
              }
            }
          ]
        });
      });
    }
  }
  
  return components;
}

// Function to fetch full template details
async function fetchFullTemplateDetails(templateId: string): Promise<MarketingTemplate> {
  try {
    console.log('Fetching full template details for:', templateId);
    
    const template = await getTemplate(templateId);
    console.log('Full template response:', template);
    
    // Extract body text from components
    const body = Array.isArray(template?.components)
      ? template.components.find((c: any) => c.type === "BODY")?.text
      : template?.content ?? template?.body ?? "";
    
    // Check if template has image header
    const imageHeader = Array.isArray(template?.components)
      ? template.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")
      : null;
    
    const result = {
      id: String(template?.id ?? template?._id ?? templateId),
      name: String(template?.name ?? template?.templateName ?? ""),
      language: template?.language ?? template?.lang,
      category: template?.category ?? template?.type,
      status: String(template?.status ?? template?.state ?? "Unknown"),
      content: String(body ?? ""),
      components: template?.components || [],
    };
    
    console.log('Processed template result:', result);
    return result;
  } catch (error) {
    console.error('Error fetching template details:', error);
    throw error;
  }
}

export default function MarketingSend() {
  const navigate = useNavigate();
  
  // Get wabaId and phoneNumberId from session storage
  const wabaId = sessionStorage.getItem('wabaId');
  const phoneNumberId = sessionStorage.getItem('phoneNumberId');
  
  console.log('MarketingSend: wabaId from session storage:', wabaId);
  console.log('MarketingSend: phoneNumberId from session storage:', phoneNumberId);
  
  // State for templates
  const [templates, setTemplates] = useState<MarketingTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MarketingTemplate | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templatesLoading, setTemplatesLoading] = useState(true);
  
  // State for phone numbers
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(true);
  
  // State for phone groups
  const [phoneGroups, setPhoneGroups] = useState<any[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [phoneGroupsLoading, setPhoneGroupsLoading] = useState(true);
  
  // State for selected group and its phone numbers
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [groupPhoneNumbers, setGroupPhoneNumbers] = useState<any[]>([]);
  const [selectedGroupPhoneNumbers, setSelectedGroupPhoneNumbers] = useState<string[]>([]);
  const [groupPhoneNumbersLoading, setGroupPhoneNumbersLoading] = useState(false);
  const [imageVersion, setImageVersion] = useState(0); // Version counter to force image reloads
  
  // State for sending
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for form
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [scheduleMode, setScheduleMode] = useState<'now' | 'later'>('now');
  const [recipientMode, setRecipientMode] = useState<'groups'>('groups');
  
  // State for media selection popup
  const [showMediaPopup, setShowMediaPopup] = useState(false);
  
  // State for group selection popup
  const [showGroupPopup, setShowGroupPopup] = useState(false);

  // State for collapsing recipients section
  const [recipientsCollapsed, setRecipientsCollapsed] = useState(false);
  // State for side collapse (hiding right panel)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);

  // Handle media selection
  const handleMediaSelect = (url: string) => {
    setVariables(prev => ({ ...prev, 'header_url': url }));
    setShowMediaPopup(false);
  };

  // Handle group selection
  const handleGroupSelect = (group: any) => {
    setSelectedGroup(group);
    setShowGroupPopup(false);
    // Automatically fetch phone numbers for the selected group
    fetchGroupPhoneNumbers(group.id);
  };

  // Fetch approved templates
  const fetchApprovedTemplates = async () => {
    setTemplatesLoading(true);
    try {
      console.log('Fetching templates for wabaId:', wabaId);
      
      // Fetch templates from the API
      const data = await getTemplatesList(wabaId);
      console.log('Raw API response:', data);
      
      // Filter only approved templates (no category filter)
      const approvedTemplates = (data.data || []).filter((template: any) => 
        template.status === 'APPROVED'
      );
      
      console.log('Approved templates found:', approvedTemplates);
      console.log('Total templates in response:', data.data?.length || 0);
      
      // If no approved templates found, show all templates for debugging
      if (approvedTemplates.length === 0 && data.data && data.data.length > 0) {
        console.log('No approved templates found, showing all templates:', data.data);
        setTemplates(data.data);
      } else {
        setTemplates(approvedTemplates);
      }
      
      // Don't auto-select - let user choose from dropdown
      
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err?.message || 'Failed to fetch approved templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Fetch phone numbers
  const fetchPhoneNumbers = async () => {
    setPhoneNumbersLoading(true);
    try {
      const phoneNumber = sessionStorage.getItem('display_phone_number') || localStorage.getItem('display_phone_number');
      if (!phoneNumber) {
        throw new Error('No phone number found. Please log in again.');
      }

      const accounts = await getWhatsappAccount(phoneNumber);
      
      // Transform accounts to phone numbers format
      const transformedPhoneNumbers: PhoneNumber[] = accounts.map((account: any, index: number) => ({
        id: account.phoneNumberId || `phone_${index}`,
        phoneNumber: account.phoneNumber || `+${account.phoneNumberId}`,
        phoneNumberId: account.phoneNumberId,
        businessName: account.businessName || `Business ${account.businessId}`,
        status: account.status || 'active',
      }));

      setPhoneNumbers(transformedPhoneNumbers);
      
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch phone numbers');
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  // Fetch phone groups
  const fetchPhoneGroups = async () => {
    setPhoneGroupsLoading(true);
    try {
      const groups = await getPhoneGroups(wabaId);
      
      // Extract groups from the nested response structure
      const groupsData = groups?.data?.groups || [];
      setPhoneGroups(groupsData);
      
    } catch (err: any) {
      console.error('Error fetching phone groups:', err);
      setError(err?.message || 'Failed to fetch phone groups');
    } finally {
      setPhoneGroupsLoading(false);
    }
  };

  // Fetch phone numbers for selected group using /all endpoint to get all data
  const fetchGroupPhoneNumbers = async (groupId: string) => {
    setGroupPhoneNumbersLoading(true);
    try {
      const response = await getGroupPhoneNumbersAll(groupId);
      
      console.log('Raw API response from getGroupPhoneNumbersAll:', response);
      
      // Handle different response structures - try multiple paths
      let phoneNumbers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        phoneNumbers = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          // response.data is an array
          phoneNumbers = response.data;
        } else if (response.data?.phoneNumbers && Array.isArray(response.data.phoneNumbers)) {
          // response.data.phoneNumbers is an array
          phoneNumbers = response.data.phoneNumbers;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          // Nested data structure
          phoneNumbers = response.data.data;
        }
      } else if (response?.phoneNumbers && Array.isArray(response.phoneNumbers)) {
        // response.phoneNumbers is an array
        phoneNumbers = response.phoneNumbers;
      } else if (response?.results && Array.isArray(response.results)) {
        // response.results is an array
        phoneNumbers = response.results;
      } else if (response && typeof response === 'object') {
        // Try to extract array from object values
        const values = Object.values(response);
        const arrayValue = values.find(v => Array.isArray(v));
        if (arrayValue) {
          phoneNumbers = arrayValue as any[];
        }
      }
      
      console.log('Extracted phoneNumbers array:', phoneNumbers);
      
      // Transform API response - preserve ALL fields from the API
      const transformedPhoneNumbers = phoneNumbers.map((phone: any, index: number) => {
        // Try multiple possible fields for the actual phone number
        const actualPhoneNumber = phone.phoneNumber || phone.phone_number || phone.number || phone.phone || '';
        const phoneId = phone.id || phone.phoneNumberId || phone._id || `phone_${Date.now()}_${index}`;
        
        console.log(`Processing phone ${index}:`, {
          rawPhone: phone,
          extractedPhoneNumber: actualPhoneNumber,
          extractedId: phoneId
        });
        
        // Preserve ALL fields from the API response, just ensure id and phoneNumber are set
        return {
          ...phone, // Keep all original fields (course, date, image, status, tags, etc.)
          id: phoneId,
          phoneNumber: actualPhoneNumber, // Ensure phoneNumber is set
          // Ensure these common fields exist
          name: phone.name || phone.contactName || phone.name || '',
          email: phone.email || '',
          tags: phone.tags || [],
          addedAt: phone.addedAt || phone.createdAt || phone.addedAt || new Date().toISOString()
        };
      });
      
      console.log('Transformed phone numbers (with all fields):', transformedPhoneNumbers);
      
      setGroupPhoneNumbers(transformedPhoneNumbers);
      
      // Auto-select all phone numbers
      setSelectedGroupPhoneNumbers(transformedPhoneNumbers.map((phone: any) => phone.id));
      
      // Increment image version to force all images to reload
      setImageVersion(prev => prev + 1);
      
    } catch (err: any) {
      console.error('Error fetching group phone numbers:', err);
      setError(err?.message || 'Failed to fetch group phone numbers');
    } finally {
      setGroupPhoneNumbersLoading(false);
    }
  };

  useEffect(() => {
    console.log('useEffect triggered, wabaId:', wabaId);
    if (wabaId) {
      console.log('wabaId exists, calling fetchApprovedTemplates');
      fetchApprovedTemplates();
      fetchPhoneNumbers();
      fetchPhoneGroups();
      
      // Check if a group was selected from phone groups manager
      const selectedGroupData = sessionStorage.getItem('selectedGroup');
      if (selectedGroupData) {
        try {
          const group = JSON.parse(selectedGroupData);
          setSelectedGroup(group);
          fetchGroupPhoneNumbers(group.id);
          setRecipientMode('groups'); // Switch to groups tab
          
          // Clear the selected group from session storage
          sessionStorage.removeItem('selectedGroup');
        } catch (err) {
          console.error('Error parsing selected group:', err);
        }
      }
    }
  }, [wabaId]);

  // Handle template selection from dropdown
  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId) {
      setSelectedTemplate(null);
      setSelectedTemplateId('');
      setVariables({});
      return;
    }
    
    setSelectedTemplateId(templateId);
    
    try {
      const fullTemplate = await fetchFullTemplateDetails(templateId);
      console.log('Full template details:', fullTemplate);
      setSelectedTemplate(fullTemplate);
      // Reset variables when template changes
      setVariables({});
    } catch (error) {
      console.error('Error fetching template details:', error);
      setError('Failed to load template details');
    }
  };

  // Extract variables from template content
  const templateVariables = selectedTemplate && selectedTemplate.content ? 
    (selectedTemplate.content.match(/\{\{([^}]+)\}\}/g) || []).map(v => v.replace(/\{\{|\}\}/g, '')) : 
    [];

  // Function to replace variables in template content
  const replaceTemplateVariables = (content: string) => {
    if (!content) return content;
    
    return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
      return variables[variable] || match;
    });
  };

  // Extract variable names from template example body_text
  const getTemplateVariableNames = (): string[] => {
    if (!selectedTemplate) return [];
    
    const bodyComponent = selectedTemplate.components?.find((c: any) => c.type === "BODY");
    if (!bodyComponent?.example?.body_text) return [];
    
    // body_text is an array of arrays, get the first array
    const variableNames = Array.isArray(bodyComponent.example.body_text[0]) 
      ? bodyComponent.example.body_text[0] 
      : bodyComponent.example.body_text;
    
    return Array.isArray(variableNames) ? variableNames : [];
  };

  // Create dynamic mapping from phone number columns to template variables
  const mapPhoneToTemplateVariables = (phone: any): Record<string, string> => {
    const variableNames = getTemplateVariableNames();
    const mapping: Record<string, string> = {};
    
    // Create mapping: {{1}} -> first variable name, {{2}} -> second, etc.
    variableNames.forEach((varName: string, index: number) => {
      const varNum = String(index + 1); // 1, 2, 3, etc.
      
      // Try to find matching column in phone object (case-insensitive)
      const phoneKey = Object.keys(phone).find(key => 
        key.toLowerCase() === varName.toLowerCase() ||
        key.toLowerCase().includes(varName.toLowerCase()) ||
        varName.toLowerCase().includes(key.toLowerCase())
      );
      
      if (phoneKey) {
        mapping[varNum] = String(phone[phoneKey] || '');
      } else {
        // Fallback: try common mappings
        if (varName.toLowerCase().includes('name')) {
          mapping[varNum] = String(phone.name || phone.contactName || '');
        } else if (varName.toLowerCase().includes('course')) {
          mapping[varNum] = String(phone.course || '');
        } else if (varName.toLowerCase().includes('date')) {
          mapping[varNum] = String(phone.date || phone.createdAt || '');
        } else if (varName.toLowerCase().includes('location') || varName.toLowerCase().includes('address')) {
          mapping[varNum] = String(phone.location || phone.address || '');
        } else if (varName.toLowerCase().includes('website') || varName.toLowerCase().includes('url')) {
          mapping[varNum] = String(phone.website || phone.url || '');
        } else if (varName.toLowerCase().includes('phone')) {
          mapping[varNum] = String(phone.phoneNumber || phone.phone || '');
        } else {
          mapping[varNum] = String(phone[varName] || phone[varName.toLowerCase()] || '');
        }
      }
    });
    
    // Always include header_url for image headers - prioritize phone's image over template example
    // Check multiple possible field names for image
    const phoneImage = phone.image || 
                      phone.imageUrl || 
                      phone.image_url ||
                      phone.avatar || 
                      phone.photo ||
                      phone.picture ||
                      '';
    
    // Use phone's image if available, otherwise use manually set variable, otherwise use template example
    mapping['header_url'] = phoneImage || variables['header_url'] || '';
    
    // Include any manually set variables
    return { ...mapping, ...variables };
  };

  // Check if a value is an image URL
  const isImageUrl = (value: any): boolean => {
    if (!value || typeof value !== 'string') return false;
    if (value === '--' || value.trim() === '') return false;
    
    // Check if it starts with http:// or https://
    const isHttpUrl = /^https?:\/\//i.test(value);
    if (!isHttpUrl) return false;
    
    // Check if it's a URL
    try {
      const url = new URL(value);
      // Check if it's an image URL by extension
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
      const hasImageExtension = imageExtensions.some(ext => 
        url.pathname.toLowerCase().endsWith(ext)
      );
      
      // Also check if URL contains 'image' in path
      const hasImageInPath = url.pathname.toLowerCase().includes('image') || 
                            url.pathname.toLowerCase().includes('/image/');
      
      return hasImageExtension || hasImageInPath;
    } catch {
      // If URL parsing fails, check if it looks like an image URL by pattern
      return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i.test(value) || 
             /\/image\//i.test(value);
    }
  };

  // Handle phone number selection
  const handlePhoneNumberToggle = (phoneNumberId: string) => {
    setSelectedPhoneNumbers(prev => 
      prev.includes(phoneNumberId) 
        ? prev.filter(id => id !== phoneNumberId)
        : [...prev, phoneNumberId]
    );
  };

  // Handle group selection
  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Handle group phone number selection
  const handleGroupPhoneNumberToggle = (phoneNumberId: string) => {
    console.log('Toggling phone number:', phoneNumberId);
    console.log('Current selected:', selectedGroupPhoneNumbers);
    
    setSelectedGroupPhoneNumbers(prev => {
      const newSelection = prev.includes(phoneNumberId) 
        ? prev.filter(id => id !== phoneNumberId)
        : [...prev, phoneNumberId];
      
      console.log('New selection:', newSelection);
      return newSelection;
    });
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (selectedGroup) {
      if (selectedGroupPhoneNumbers.length === groupPhoneNumbers.length) {
        setSelectedGroupPhoneNumbers([]);
      } else {
        setSelectedGroupPhoneNumbers(groupPhoneNumbers.map((phone: any) => phone.id));
      }
    } else {
      if (selectedGroups.length === phoneGroups.length) {
        setSelectedGroups([]);
      } else {
        setSelectedGroups(phoneGroups.map((g: any) => g.id));
      }
    }
  };

  // Send marketing messages
  const handleSendMarketing = async () => {
    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }

    if (selectedGroup && selectedGroupPhoneNumbers.length === 0) {
      setError('Please select at least one phone number from the group');
      return;
    }

    if (!selectedGroup && selectedGroups.length === 0) {
      setError('Please select at least one phone group');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      // Build components with flow logic
      const components = buildWAComponentsForMarketing(variables, selectedTemplate);
      
      // Map selected phone number IDs to actual phone numbers
      let phoneNumbersArray: string[] = [];
      
      if (selectedGroup && selectedGroupPhoneNumbers.length > 0) {
        console.log('Selected phone number IDs:', selectedGroupPhoneNumbers);
        console.log('Available group phone numbers:', groupPhoneNumbers);
        
        // Get phone numbers from selected group phone numbers
        phoneNumbersArray = groupPhoneNumbers
          .filter((phone: any) => selectedGroupPhoneNumbers.includes(phone.id))
          .map((phone: any) => {
            console.log('Processing phone object:', phone);
            
            // The phone number should be stored in phoneNumber property
            // This is what we set when transforming the API response
            let phoneNum = phone.phoneNumber;
            
            console.log('Phone object phoneNumber property:', phoneNum);
            
            // If phoneNumber is missing or is an ID format, this is a problem
            if (!phoneNum || phoneNum.startsWith('phone_') || phoneNum === phone.id) {
              console.error('ERROR: Phone number is missing or invalid!', {
                phoneId: phone.id,
                phoneNumber: phoneNum,
                fullPhoneObject: phone
              });
              console.warn('Phone object does not have a valid phoneNumber. Available keys:', Object.keys(phone));
              return null;
            }
            
            // Remove any non-digit characters except +
            phoneNum = String(phoneNum).replace(/[^\d+]/g, '');
            
            // Add + prefix if not present
            if (!phoneNum.startsWith('+')) {
              phoneNum = `+${phoneNum}`;
            }
            
            // Final validation
            if (!phoneNum || phoneNum === '+' || phoneNum.length < 8) {
              console.warn('Invalid phone number after formatting:', phoneNum, 'from object:', phone);
              return null;
            }
            
            console.log('Final formatted phone number:', phoneNum);
            return phoneNum;
          })
          .filter((phone: string | null): phone is string => phone !== null && phone !== '+' && phone.length >= 8); // Remove null and invalid numbers
        
        console.log('Final phone numbers array to send:', phoneNumbersArray);
      } else if (selectedGroups.length > 0) {
        // If using groups instead of individual phone numbers, keep as group IDs
        // Or if you want to expand groups to phone numbers, fetch them here
        phoneNumbersArray = selectedGroups;
      }
      
      if (phoneNumbersArray.length === 0) {
        setSending(false);
        setError('Please select at least one phone number');
        return;
      }
      
      // Get businessId from session storage (WABA ID is the business account ID)
      const businessId = wabaId || sessionStorage.getItem('wabaId');
      
      // Validate phoneNumberId is available
      if (!phoneNumberId) {
        setSending(false);
        setError('WhatsApp Business Phone Number ID is missing. Please log in again.');
        return;
      }

      // Validate that a group is selected (required for schedule API)
      if (!selectedGroup) {
        setSending(false);
        setError('Please select a group to send messages');
        return;
      }

      // Determine trigger time based on schedule mode
      // Send local time as-is with timezone - backend will handle conversion
      let triggerTime: string;
      if (scheduleMode === 'later') {
        // Validate scheduled time
        if (!scheduledTime) {
          setSending(false);
          setError('Please select a scheduled time');
          return;
        }
        // Format local time with timezone offset (backend will convert)
        const date = new Date(scheduledTime);
        const offset = -date.getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offset) / 60);
        const offsetMinutes = Math.abs(offset) % 60;
        const offsetSign = offset >= 0 ? '+' : '-';
        const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
        
        // Format: YYYY-MM-DDTHH:mm:ss with timezone offset
        triggerTime = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}${offsetStr}`;
      } else {
        // Send Now - current local time
        const date = new Date();
        const offset = -date.getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offset) / 60);
        const offsetMinutes = Math.abs(offset) % 60;
        const offsetSign = offset >= 0 ? '+' : '-';
        const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
        
        triggerTime = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}${offsetStr}`;
      }
      
      // Get user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      console.log('=== SCHEDULING GROUP MESSAGE ===');
      console.log('Trigger Time (local):', triggerTime);
      console.log('Timezone:', timezone);

      // Always call schedule-message API with timezone
      const result = await scheduleGroupMessage(selectedGroup.id, selectedTemplate.id, triggerTime, timezone);
      
      if (scheduleMode === 'later') {
        setSuccess(`Message scheduled successfully! Will be sent at ${new Date(scheduledTime).toLocaleString()}.`);
      } else {
        setSuccess(`Message sent successfully! ${selectedGroupPhoneNumbers.length} messages queued.`);
      }
      
      // Reset form
      setTimeout(() => {
        setSelectedPhoneNumbers([]);
        setSelectedGroups([]);
        setVariables({});
        setScheduledTime('');
        setScheduleMode('now');
        setSuccess(null);
      }, 3000);

    } catch (err: any) {
      setError(err?.message || 'Failed to send marketing messages');
    } finally {
      setSending(false);
    }
  };

  // Remove the early return for loading - let the UI render with overlay

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Send Marketing Messages</h1>
              <p className="text-lg text-gray-600">Select templates and phone numbers for bulk marketing campaigns</p>
              {wabaId && (
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                  <span className="mr-2">📱</span>
                  WABA ID: {wabaId}
                </div>
              )}
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/events/whatsapp-marketing-list')}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium"
              >
                <span className="mr-2">📋</span>
                Marketing List
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-700 shadow-sm">
            <div className="flex items-center">
              <span className="mr-3 text-xl">⚠️</span>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-6 py-4 text-green-700 shadow-sm">
            <div className="flex items-center">
              <span className="mr-3 text-xl">✅</span>
              <span className="font-medium">{success}</span>
            </div>
          </div>
        )}

        <div className={`grid gap-8 transition-all duration-300 ${rightPanelCollapsed ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {/* Left: Template Selection */}
          <div className={`rounded-2xl border border-gray-100 bg-white p-8 shadow-lg ${rightPanelCollapsed ? 'lg:col-span-1' : ''}`}>
            <h2 className="mb-6 text-xl font-semibold text-gray-900">1. Select Approved Template</h2>
            
            {/* Template Dropdown */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Template</label>
              {templatesLoading ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-500">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Loading templates...</span>
                  </div>
                </div>
              ) : (
                <select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">— Select a template —</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {template.category ? ` ${template.category}` : ""}
                    </option>
                  ))}
                </select>
              )}
              {templates.length === 0 && !templatesLoading && (
                <div className="mt-2 text-sm text-gray-500">
                  No approved templates found. Please create and approve templates first.
                </div>
              )}
            </div>

            {/* Template Preview - User-wise */}
            {selectedTemplate && (
              <div className="mt-6">
                <h3 className="mb-3 text-sm font-medium text-gray-700">Template Preview</h3>
                {selectedGroup && groupPhoneNumbers.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="mb-3 text-xs text-gray-600">
                      Showing {groupPhoneNumbers.filter(p => selectedGroupPhoneNumbers.includes(p.id)).length} preview{groupPhoneNumbers.filter(p => selectedGroupPhoneNumbers.includes(p.id)).length !== 1 ? 's' : ''} for selected recipients
                    </div>
                    <div className="grid grid-cols-1 gap-3 max-h-[500px] overflow-y-auto">
                      {groupPhoneNumbers
                        .filter(phone => selectedGroupPhoneNumbers.includes(phone.id))
                        .map((phone) => {
                          // Use dynamic mapping based on template example body_text
                          const recipientVariables = mapPhoneToTemplateVariables(phone);

                          // Replace template variables {{1}}, {{2}}, etc.
                          const bodyText = selectedTemplate.components?.find((c: any) => c.type === "BODY")?.text || selectedTemplate.content || "";
                          const replacedText = bodyText.replace(/\{\{(\d+)\}\}/g, (match: string, num: string) => {
                            return recipientVariables[num] || match;
                          });

                          const headerComponent = selectedTemplate.components?.find((c: any) => c.type === "HEADER" && c.format === "IMAGE");
                          const headerTextComponent = selectedTemplate.components?.find((c: any) => c.type === "HEADER" && c.format === "TEXT");
                          const footerComponent = selectedTemplate.components?.find((c: any) => c.type === "FOOTER");
                          const buttonsComponent = selectedTemplate.components?.find((c: any) => c.type === "BUTTONS");
                          
                          // Prioritize phone's image over template example image
                          // First, try direct field names (case-insensitive search)
                          let phoneImage = '';
                          
                          // Check all possible image field names
                          const imageFieldNames = ['image', 'imageUrl', 'image_url', 'Image', 'ImageUrl', 'IMAGE', 'avatar', 'photo', 'picture'];
                          for (const fieldName of imageFieldNames) {
                            if (phone[fieldName] && typeof phone[fieldName] === 'string' && phone[fieldName].trim() !== '') {
                              phoneImage = phone[fieldName].trim();
                              break;
                            }
                          }
                          
                          // If not found, search for any field containing "image" (case-insensitive)
                          if (!phoneImage) {
                            const imageKey = Object.keys(phone).find(key => 
                              key.toLowerCase().includes('image') && 
                              phone[key] && 
                              typeof phone[key] === 'string' &&
                              String(phone[key]).trim() !== '' &&
                              String(phone[key]).trim() !== '--' &&
                              (String(phone[key]).startsWith('http://') || String(phone[key]).startsWith('https://'))
                            );
                            if (imageKey) {
                              phoneImage = String(phone[imageKey]).trim();
                            }
                          }
                          
                          // Final validation - ensure it's a valid URL
                          if (phoneImage && !phoneImage.startsWith('http://') && !phoneImage.startsWith('https://')) {
                            phoneImage = '';
                          }
                          
                          console.log('Phone image check:', {
                            phoneId: phone.id,
                            phoneName: phone.name,
                            phoneImage: phoneImage || 'NOT FOUND',
                            hasImage: !!phoneImage,
                            allKeys: Object.keys(phone),
                            imageFields: Object.keys(phone).filter(k => {
                              const val = phone[k];
                              return k.toLowerCase().includes('image') && val && typeof val === 'string' && val.trim() !== '';
                            })
                          });
                          
                          // Use phone's image first, then manually set variable, then template example

                          return (
                            <div key={phone.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* WhatsApp-style message bubble */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                  {/* Header Image - Only show if template has image header component */}
                                  {headerComponent && (() => {
                                    // Prioritize phoneImage - use it if it's a valid URL
                                    const hasPhoneImage = phoneImage && phoneImage.trim() !== '' && 
                                                         (phoneImage.startsWith('http://') || phoneImage.startsWith('https://'));
                                    
                                    // Use phone's image if available, otherwise fall back to template example
                                    const imageToUse = hasPhoneImage 
                                      ? phoneImage 
                                      : (headerComponent?.example?.header_handle?.[0] || '');
                                    
                                    if (!imageToUse) {
                                      return null;
                                    }
                                    
                                    // Add cache-busting parameter ONLY for phone images to force reload
                                    // Use imageVersion + phone ID hash to ensure all images reload when phone numbers are fetched
                                    const imageUrlHash = imageToUse.split('/').pop()?.split('?')[0] || '';
                                    // Create a stable but unique cache buster based on phone ID and image URL
                                 
                                 
                                    
                                    // Create a unique key that includes phone ID, image hash, version, and random number
                                    // Random number ensures each image in the loop gets a completely unique key
                                    const randomNum = Math.random().toString(36).substring(2, 9);
                                    const fullImageKey = `${phone.id}-${imageUrlHash}-v${phone.id}-${randomNum}`;
                                    const imageKey = `img-${fullImageKey.substring(0, 120)}`;
                                    
                                    return (
                                      <div className="w-full h-24" key={`wrapper-${imageKey}`}>
                                        <img 
                                          key={imageKey}
                                          src={imageToUse}
                                        alt="Template header"
                                        className="w-full h-full object-cover"
                                          loading="eager"
                                          decoding="async"
                                      />
                                    </div>
                                    );
                                  })()}
                                  
                                  {/* Header Text */}
                                  {headerTextComponent?.text && (
                                    <div className="px-2 pt-2">
                                      <div className="text-xs font-semibold text-gray-900">
                                        {headerTextComponent.text}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Body Text */}
                                  <div className="p-2">
                                    <div className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                      {replacedText}
                                    </div>
                                  </div>
                                  
                                  {/* Buttons - Dynamic rendering based on template */}
                                  {buttonsComponent?.buttons && Array.isArray(buttonsComponent.buttons) && buttonsComponent.buttons.length > 0 && (
                                    <div className="px-2 pb-2 space-y-1">
                                      {buttonsComponent.buttons.map((button: any, btnIdx: number) => {
                                        // QUICK_REPLY buttons
                                        if (button.type === 'QUICK_REPLY') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-medium text-center"
                                            >
                                              {button.text}
                                            </div>
                                          );
                                        }
                                        // URL buttons
                                        else if (button.type === 'URL') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium text-center"
                                            >
                                              {button.text} 🔗
                                            </div>
                                          );
                                        }
                                        // PHONE_NUMBER buttons
                                        else if (button.type === 'PHONE_NUMBER') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-purple-700 font-medium text-center"
                                            >
                                              {button.text} 📞
                                            </div>
                                          );
                                        }
                                        // FLOW buttons
                                        else if (button.type === 'FLOW') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 font-medium text-center"
                                            >
                                              {button.text} 🔄
                                              {button.flow_id && (
                                                <div className="text-xs text-orange-600 mt-1">
                                                  Flow ID: {button.flow_id}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }
                                        // Fallback for any other button types
                                        return (
                                          <div
                                            key={btnIdx}
                                            className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium text-center"
                                          >
                                            {button.text || `Button ${btnIdx + 1}`}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Footer */}
                                  {footerComponent?.text && (
                                    <div className="px-2 pb-2">
                                      <div className="text-xs text-gray-500 italic">
                                        {footerComponent.text}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-3 max-w-sm mx-auto">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800 mb-3">
                      Select recipients from a group to see personalized previews
                    </div>
                    {/* Default preview with manual variables */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      {/* Header Image */}
                    {selectedTemplate?.components?.some((c: any) => c.type === "HEADER" && c.format === "IMAGE") && (
                      <div className="w-full">
                        <img 
                          src={variables['header_url'] || selectedTemplate.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")?.example?.header_handle?.[0]}
                          alt="Template header"
                          className="w-full h-32 object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                      
                      {/* Header Text */}
                      {selectedTemplate.components?.find((c: any) => c.type === "HEADER" && c.format === "TEXT")?.text && (
                        <div className="px-3 pt-3">
                          <div className="text-sm font-semibold text-gray-900">
                            {selectedTemplate.components.find((c: any) => c.type === "HEADER" && c.format === "TEXT")?.text}
                          </div>
                      </div>
                    )}
                    
                    {/* Body Text */}
                    <div className="p-3">
                      <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                          {(selectedTemplate.components?.find((c: any) => c.type === "BODY")?.text || selectedTemplate.content || "").replace(/\{\{(\d+)\}\}/g, (match: string, num: string) => {
                            return variables[num] || match;
                          })}
                      </div>
                    </div>
                      
                      {/* Buttons - Dynamic rendering based on template */}
                      {selectedTemplate.components?.find((c: any) => c.type === "BUTTONS")?.buttons && Array.isArray(selectedTemplate.components.find((c: any) => c.type === "BUTTONS")?.buttons) && (
                        <div className="px-3 pb-3 space-y-2">
                          {selectedTemplate.components.find((c: any) => c.type === "BUTTONS")?.buttons.map((button: any, btnIdx: number) => {
                            // QUICK_REPLY buttons
                            if (button.type === 'QUICK_REPLY') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-medium text-center"
                                >
                                  {button.text}
                                </div>
                              );
                            }
                            // URL buttons
                            else if (button.type === 'URL') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium text-center"
                                >
                                  {button.text} 🔗
                                </div>
                              );
                            }
                            // PHONE_NUMBER buttons
                            else if (button.type === 'PHONE_NUMBER') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-purple-700 font-medium text-center"
                                >
                                  {button.text} 📞
                                </div>
                              );
                            }
                            // FLOW buttons
                            else if (button.type === 'FLOW') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 font-medium text-center"
                                >
                                  {button.text} 🔄
                                  {button.flow_id && (
                                    <div className="text-xs text-orange-600 mt-1">
                                      Flow ID: {button.flow_id}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            // Fallback for any other button types
                            return (
                              <div
                                key={btnIdx}
                                className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium text-center"
                              >
                                {button.text || `Button ${btnIdx + 1}`}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    
                    {/* Footer */}
                      {selectedTemplate.components?.find((c: any) => c.type === "FOOTER") && (
                      <div className="px-3 pb-3">
                        <div className="text-xs text-gray-500 border-t pt-2">
                            {selectedTemplate.components.find((c: any) => c.type === "FOOTER")?.text}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Phone Numbers & Groups Selection */}
          {!rightPanelCollapsed && (
          <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-lg">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">2. Select Recipients</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setRecipientsCollapsed(!recipientsCollapsed)}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                    title={recipientsCollapsed ? "Expand" : "Collapse"}
                  >
                    {recipientsCollapsed ? (
                      <>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        <span>Expand</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        <span>Collapse</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setRightPanelCollapsed(true)}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                    title="Close panel"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            
            {/* Phone Groups Selection */}
            {!recipientsCollapsed && (
              <div>
                {selectedGroup ? (
                  /* Show selected group's phone numbers */
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Selected Group: {selectedGroup.name}</h4>
                        <p className="text-sm text-gray-600">{selectedGroup.description}</p>
                      </div>
                      <button
                        onClick={() => setShowGroupPopup(true)}
                        className="rounded-md bg-blue-100 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-200"
                      >
                        Change Group
                      </button>
                    </div>

                    {groupPhoneNumbersLoading ? (
                      <LoadingComponent 
                        loading={groupPhoneNumbersLoading}
                        message="Loading phone numbers..."
                        showRefreshButton={false}
                        size="medium"
                      />
                    ) : (
                      <div className="max-h-64 space-y-2 overflow-y-auto">
                        {groupPhoneNumbers.map((phone) => {
                          console.log('Rendering phone:', phone.id, phone.phoneNumber);
                          return (
                            <div
                              key={phone.id}
                              className="rounded-lg border border-gray-200 p-3 bg-gray-50"
                            >
                              <div className="space-y-1">
                                {/* Primary info */}
                                <div className="font-medium text-gray-900">
                                  {phone.name || phone.phoneNumber || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-600">{phone.phoneNumber}</div>
                                
                                {/* Show tags as badges if they exist */}
                                {phone.tags && Array.isArray(phone.tags) && phone.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {phone.tags.map((tag: string, index: number) => (
                                      <span 
                                        key={index} 
                                        className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full font-medium"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Show all available columns */}
                                <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                  {Object.keys(phone)
                                    .filter(key => 
                                      key !== 'id' && 
                                      key !== 'phoneNumber' && 
                                      key !== 'name' &&
                                      key !== 'tags' && // Exclude tags as they're shown above
                                      phone[key] !== null && 
                                      phone[key] !== undefined && 
                                      phone[key] !== ''
                                    )
                                    .map((key) => (
                                      <div key={key} className="text-xs text-gray-600 flex items-start">
                                        <span className="font-medium capitalize mr-2 min-w-[80px]">{key}:</span>
                                        <span className="break-words flex-1">
                                          {key.toLowerCase().includes('image') && isImageUrl(phone[key]) ? (
                                            <img 
                                              src={phone[key]} 
                                              alt={key}
                                              className="w-12 h-12 object-cover rounded border border-gray-200"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const parent = e.currentTarget.parentElement;
                                                if (parent) {
                                                  parent.textContent = String(phone[key]);
                                                }
                                              }}
                                            />
                                          ) : (
                                            String(phone[key])
                                          )}
                                        </span>
                              </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {groupPhoneNumbers.length === 0 && !groupPhoneNumbersLoading && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-500">
                        <p>No phone numbers found in this group.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Show group selection */
                  <div>
                    <div className="mb-4">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setShowGroupPopup(true)}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span>Select Group</span>
                        </button>
                        {selectedGroup && (
                          <div className="flex-1">
                            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border">
                              <span className="font-medium">Selected:</span> {selectedGroup.name} ({selectedGroup.totalCount} numbers)
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {phoneGroupsLoading ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-500">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span>Loading phone groups...</span>
                        </div>
                      </div>
                    ) : !selectedGroup ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-500">
                        <svg className="h-12 w-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="mb-2">No group selected</p>
                        <p className="text-sm">Click "Select Group" to choose a phone group</p>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
            
            {/* Collapsed view - show summary */}
            {recipientsCollapsed && selectedGroup && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
              <div>
                    <div className="font-medium text-sm text-gray-900">Selected Group: {selectedGroup.name}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {groupPhoneNumbers.length} recipient{groupPhoneNumbers.length !== 1 ? 's' : ''} (all selected)
                    </div>
                  </div>
                <button
                    onClick={() => setShowGroupPopup(true)}
                    className="rounded-md bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-200"
                >
                    Change
                </button>
              </div>
            </div>
            )}
          </div>
        )}

          {/* Collapsed right panel - show expand button */}
          {rightPanelCollapsed && (
            <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-10">
              <button
                onClick={() => setRightPanelCollapsed(false)}
                className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                title="Show recipients panel"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
          </div>
        )}
        </div>

        {/* Scheduling Options */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">3. Scheduling</h2>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleMode"
                  value="now"
                  checked={scheduleMode === 'now'}
                  onChange={(e) => setScheduleMode('now')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Send Now</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleMode"
                  value="later"
                  checked={scheduleMode === 'later'}
                  onChange={(e) => setScheduleMode('later')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Schedule for Later</span>
              </label>
            </div>

            {scheduleMode === 'later' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Time
                </label>
                <input
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none w-full"
                  required={scheduleMode === 'later'}
                />
              </div>
            )}
          </div>
        </div>

        {/* Send Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSendMarketing}
            disabled={
              !selectedTemplate || 
              (selectedGroup ? selectedGroupPhoneNumbers.length === 0 : selectedGroups.length === 0) || 
              sending ||
              (scheduleMode === 'later' && !scheduledTime)
            }
            className={`rounded-lg px-6 py-3 font-medium text-white transition ${
              !selectedTemplate || 
              (selectedGroup ? selectedGroupPhoneNumbers.length === 0 : selectedGroups.length === 0) || 
              sending ||
              (scheduleMode === 'later' && !scheduledTime)
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {sending 
              ? (scheduleMode === 'later' ? 'Scheduling...' : 'Sending...') 
              : scheduleMode === 'later' 
                ? `Schedule for ${selectedGroup ? selectedGroupPhoneNumbers.length : selectedGroups.length} recipients`
                : `Send to ${selectedGroup ? selectedGroupPhoneNumbers.length : selectedGroups.length} recipients`
            }
          </button>
        </div>
      </div>

      {/* Media Selection Popup */}
      <MediaSelectionPopup
        isOpen={showMediaPopup}
        onClose={() => setShowMediaPopup(false)}
        onSelect={handleMediaSelect}
        title="Select Header Image"
      />

      {/* Group Selection Popup */}
      <GroupSelectionPopup
        isOpen={showGroupPopup}
        onClose={() => setShowGroupPopup(false)}
        onSelect={handleGroupSelect}
        title="Select Phone Group"
      />

      {/* Loading Overlay */}
      {(templatesLoading || phoneNumbersLoading || phoneGroupsLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <LoadingComponent 
              loading={true}
              message="Loading marketing data..."
              showRefreshButton={false}
              size="large"
            />
          </div>
        </div>
      )}
    </div>
  );
}

